// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@synd/App/Components/Common/XngZone";
import { isCheckoutPage } from "@synd/App/Store/Selectors/CheckoutSelector";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // XNG-Changes
    linksAndCopyRightContainer?: InjectableCss;
    footerBottomSection?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    const simpleFooter = useAppSelector(
        state => isCheckoutPage(state) || getCurrentPage(state).type === "CreateAccountPage",
    );
    return (
        // XNG-Changes : Change Structure
        <StyledWrapper {...styles.widgetGridContainer}>
            {!simpleFooter && (
                <StyledWrapper {...styles.subscribeGridItem}>
                    <XngZone zoneName="Subscribe" contentId={id} hint="Subscribe" />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.footerBottomSection} {...{ simpleFooter }}>
                <StyledWrapper {...styles.linksAndCopyRightContainer} {...{ simpleFooter }}>
                    {!simpleFooter && (
                        <>
                            <StyledWrapper {...styles.linkList1GridItem}>
                                <XngZone zoneName="LinkList1" contentId={id} hint="LinkList1" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.linkList2GridItem}>
                                <XngZone zoneName="LinkList2" contentId={id} hint="LinkList2" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.linkList3GridItem}>
                                <XngZone zoneName="LinkList3" contentId={id} hint="LinkList3" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.linkList4GridItem}>
                                <XngZone zoneName="LinkList4" contentId={id} hint="LinkList4" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.socialLinksGridItem}>
                                <XngZone zoneName="Address" contentId={id} hint="Address" />
                                <XngZone zoneName="SocialLinks" contentId={id} hint="SocialLinks" />
                            </StyledWrapper>
                        </>
                    )}
                    <StyledWrapper {...styles.siteMapLinksGridItem}>
                        <XngZone zoneName="CopyRight" contentId={id} hint="CopyRight" />
                        <XngZone zoneName="SiteMapLinks" contentId={id} hint="SiteMapLinks" />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            {/* <GridItem {...styles.logoGridItem}>
                <StyledWrapper {...styles.logoWrapper}>
                    <Zone zoneName="Logo" contentId={id} />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.linkList1GridItem}>
                <Zone zoneName="LinkList1" contentId={id} />
            </GridItem>
            <GridItem {...styles.linkList2GridItem}>
                <Zone zoneName="LinkList2" contentId={id} />
            </GridItem>
            <GridItem {...styles.linkList3GridItem}>
                <Zone zoneName="LinkList3" contentId={id} />
            </GridItem>
            <GridItem {...styles.linkList4GridItem}>
                <Zone zoneName="LinkList4" contentId={id} />
            </GridItem>
            <GridItem {...styles.subscribeGridItem}>
                <Zone zoneName="Subscribe" contentId={id} />
            </GridItem>
            <GridItem {...styles.socialLinksGridItem}>
                <Zone zoneName="SocialLinks" contentId={id} />
            </GridItem>
            <GridItem {...styles.siteMapLinksGridItem}>
                <Zone zoneName="SiteMapLinks" contentId={id} />
            </GridItem>
            <GridItem {...styles.richContentGridItem}>
                <Zone zoneName="RichContent" contentId={id} />
            </GridItem> */}
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
